// src/components/landing/Footer.tsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Twitter } from 'lucide-react';

export const Footer: React.FC = () => {
  const location = useLocation();
  
  const scrollToScreenshots = () => {
    // Only scroll if we're on the landing page
    if (location.pathname === '/') {
      document.getElementById('screenshot-showcase')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If not on landing page, navigate to landing page and then to showcase
      window.location.href = '/#screenshot-showcase';
    }
  };

  const scrollToFAQ = () => {
    if (location.pathname === '/') {
      document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#faq';
    }
  };

  const scrollToHero = () => {
    if (location.pathname === '/') {
      document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#hero';
    }
  };
  
  return (
    <footer className="relative py-24 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 text-gray-800 rounded-t-3xl shadow-[0_-10px_50px_-5px_rgba(0,0,0,0.25)] mt-12 mx-4 md:mx-8">
      {/* Glass texture effects - no images needed */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-white/5 backdrop-blur-[100px] rounded-t-3xl pointer-events-none"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-primary-500/10 to-transparent rounded-t-3xl pointer-events-none"></div>
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary-500/15 blur-[100px] -z-10 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-blue-500/15 blur-[100px] -z-10 pointer-events-none"></div>
      <div className="absolute top-40 left-1/4 w-72 h-72 rounded-full bg-indigo-500/10 blur-[80px] -z-10 pointer-events-none"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-3 gap-16 md:gap-12">
          {/* Brand Section - Restructured with better spacing */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <div className="mb-10">
              <h3 className="font-display text-xl font-semibold text-gray-950 mb-3">
                Simplified Trading <span className="gradient-text">Journal</span>
              </h3>
              <p className="text-gray-700 leading-relaxed max-w-xs">
                Focus on the process, not the profits
              </p>
            </div>
            
            {/* Partner section with better spacing */}
            <div>
              <h4 className="font-display text-xs uppercase tracking-wider text-gray-600 mb-3">
                Become a Partner
              </h4>
              <Link
                to="/team"
                className="font-medium text-primary-700 hover:text-primary-600 transition-colors inline-block"
              >
                See Our Affiliate Program (40%)
              </Link>
            </div>
          </div>

          {/* Contact Us Section - improved spacing */}
          <div className="flex flex-col items-center text-center">
            <h4 className="font-display text-xs uppercase tracking-wider text-gray-600 mb-6">
              Contact Us
            </h4>
            <p className="text-gray-700 mb-4">
              Have questions? We're here to help.
            </p>
            
            {/* Email with better styling */}
            <div className="flex items-center text-gray-700 font-medium mb-8">
              <Mail className="mr-2 h-4 w-4 text-gray-600" />
              <span>simplifiedtradingjournal@pm.me</span>
            </div>
            
            {/* X (Twitter) Link with more space above */}
            <div className="flex items-center justify-center">
              <a 
                href="https://x.com/simplifiedtj" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-primary-700 font-medium hover:text-primary-600 transition-colors underline"
              >
                <Twitter className="mr-2 h-4 w-4" />
                Follow us on X
              </a>
              <span className="text-gray-700 ml-1">(@simplifiedtj)</span>
            </div>
          </div>

          {/* Quick Links - refined styling */}
          <div className="flex flex-col items-center md:items-end text-center md:text-right relative">
            <h4 className="font-display text-xs uppercase tracking-wider text-gray-600 mb-6">
              Quick Links
            </h4>
            <ul className="space-y-4">
              <li>
                <button
                  onClick={scrollToHero}
                  className="text-gray-700 hover:text-gray-950 transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={scrollToScreenshots}
                  className="text-gray-700 hover:text-gray-950 transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  Product
                </button>
              </li>
              <li>
                <Link
                  to="/trial"
                  className="text-gray-700 hover:text-gray-950 transition-colors"
                >
                  Free Trial
                </Link>
              </li>
              <li>
                <button
                  onClick={scrollToFAQ}
                  className="text-gray-700 hover:text-gray-950 transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  FAQ
                </button>
              </li>
              <li>
                <Link
                  to="/resources"
                  className="text-gray-700 hover:text-gray-950 transition-colors"
                >
                  Resources
                </Link>
              </li>
              <li>
                {/* Regular React Router Link for desktop */}
                <Link
                  to="/privacy-policy"
                  className="text-gray-700 hover:text-gray-950 transition-colors hidden md:inline-block"
                >
                  Privacy Policy
                </Link>
                {/* Direct href for mobile - will be shown only on mobile */}
                <a 
                  href="/privacy-policy"
                  className="text-gray-700 hover:text-gray-950 transition-colors md:hidden inline-block"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                {/* Regular React Router Link for desktop */}
                <Link
                  to="/terms"
                  className="text-gray-700 hover:text-gray-950 transition-colors hidden md:inline-block"
                >
                  Terms of Service
                </Link>
                {/* Direct href for mobile - will be shown only on mobile */}
                <a 
                  href="/terms"
                  className="text-gray-700 hover:text-gray-950 transition-colors md:hidden inline-block"
                >
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright - refined with glass card effect */}
        <div className="mt-20 pt-8 border-t border-gray-300/50 text-center">
          <p className="text-gray-600 text-sm">
            © {new Date().getFullYear()} Simplified Trading Journal. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};